import { useState, useEffect } from 'react'
import { TableCell, Select, MenuItem } from '@mui/material'
import GoodSVG from '../../assets/status/good.svg?react'
import NeutralSVG from '../../assets/status/neutral.svg?react'
import BadSVG from '../../assets/status/bad.svg?react'
import { PLANS } from '../../utils/planNames'
import { getHHmm, timestampToDDMMYYYY } from '../../utils/dateConversion'
import { useTranslation } from "react-i18next"

const DropdownTableCell = ({ data, optionType, onChange, ...props }) => {
  const { t } = useTranslation('common')
  const STATUS_OPTIONS = [
    { value: 'pending', label: t('pending'), color: '#BE3756' },
    { value: 'acknowledged', label: t('acknowledged'), color: '#B26D1D' },
    { value: 'completed', label: t('completed'), color: '#428262' },
  ]

  const SEVERITY_OPTIONS = [
    { value: 'high', label: t('high'), color: '#BE3756' },
    { value: 'medium', label: t('medium'), color: '#B26D1D' },
    { value: 'low', label: t('low'), color: '#428262' },
  ]
  const options = optionType === 'severity' ? SEVERITY_OPTIONS : STATUS_OPTIONS
  const defaultValue = optionType === 'severity' ? 'low' : 'pending'

  const [value, setValue] = useState(data || defaultValue)

  useEffect(() => {
    if (data) setValue(data)
  }, [data])

  return (
    <TableCell {...props}>
      <Select
        value={value}
        onChange={e => {
          setValue(e.target.value)
          onChange?.(e.target.value)
        }}
        displayEmpty
        sx={{
          minWidth: 120,
          fontWeight: 500,
          color: options.find(o => o.value === value)?.color || '#000',
        }}
      >
        {options.map(option => (
          <MenuItem
            key={option.value}
            value={option.value}
            sx={{ color: option.color }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </TableCell>
  )
}

const DateTableCell = ({ date, ...props }) => {
  const formatDate = date ? timestampToDDMMYYYY({ timestamp: date }) : '-'

  return <TableCell {...props}>{formatDate}</TableCell>
}

const TextTableCell = ({ text, ...props }) => {
  return (
    <TableCell
      align='left'
      {...props}
    >
      {text ? text : '-'}
    </TableCell>
  )
}

const HourTableCell = ({ hour, ...props }) => {
  const { t } = useTranslation('common')
  const convertMinutesToHours = value => {
    if (!value) return

    const hour = Math.floor(value / 60)
    const minutes = value % 60

    if (hour <= 0 && minutes <= 15) return t('now')
    if (hour <= 0 && minutes >= 15) return `${minutes} min`

    return getHHmm({ hours: hour, minutes })
  }

  return (
    <TableCell
      align='left'
      sx={{
        color: convertMinutesToHours(hour) === 'Agora' ? '#BE3756' : '#000',
        ...props?.sxx,
      }}
      {...props}
    >
      {hour ? convertMinutesToHours(hour) : '-'}
    </TableCell>
  )
}

const ActionTableCell = ({ action, ...props }) => {
  const { t } = useTranslation('common')
  const ACTION_TYPE = {
    pending: {
      color: '#BE3756',
      name: t('pending'),
    },
    no: {
      color: '#BE3756',
      name: t('no'),
    },
    scheduled: {
      color: '#BE3756',
      name: t('programmed'),
    },
    sended: {
      color: '#B26D1D',
      name: t('sent'),
    },
    sent: {
      color: '#B26D1D',
      name: t('sent'),
    },
    acknowledged: {
      color: '#B26D1D',
      name: t('understood'),
    },
    completed: {
      color: '#428262',
      name: t('completed'),
    },
    yes: {
      color: '#428262',
      name: t('yes'),
    },
    read: {
      color: '#428262',
      name: t('read'),
    },
  }

  return (
    <TableCell
      align='left'
      sx={{ color: ACTION_TYPE[action]?.color, fontWeight: 500 }}
      {...props}
    >
      {action ? ACTION_TYPE[action]?.name : '-'}
    </TableCell>
  )
}

const ValueTableCell = ({ value, ...props }) => {
  const { t } = useTranslation('common')
  const getBGColors = ({ data, isSleeping }) => {
    if (isSleeping) return '#93b7ff'

    if (typeof data !== 'number' || !data) return

    // if (data > 90) return '#62E751'
    // if (data > 75 && data <= 90) return '#71e74b'
    // if (data > 60 && data <= 75) return '#9be837'
    // if (data > 50 && data <= 60) return '#ffe000'
    // if (data > 40 && data <= 50) return '#ffbd00'
    // if (data > 30 && data <= 40) return '#ffa022'
    // if (data <= 30) return '#fd564e'

    if (data > 94) return '#62E751'
    if (data > 87 && data <= 94) return '#71e74b'
    if (data > 80 && data <= 87) return '#9be837'
    if (data > 75 && data <= 80) return '#ffe000'
    if (data > 67 && data <= 75) return '#ffbd00'
    if (data > 50 && data <= 67) return '#ffa022'
    if (data <= 50) return '#fd564e'
  }

  if (props?.planName === PLANS.NOW) {
    const status = {
      1: {
        svg: (
          <GoodSVG
            width={40}
            height={35}
          />
        ),
        text: t('good'),
        color: '#428262',
      },
      2: {
        svg: (
          <NeutralSVG
            width={40}
            height={35}
          />
        ),
        text: t('regular'),
        color: '#B26D1D',
      },
      3: {
        svg: (
          <BadSVG
            width={40}
            height={35}
          />
        ),
        text: t('bad'),
        color: '#BE3756',
      },
    }

    return (
      <TableCell
        align='left'
        {...props}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: 8,
          }}
        >
          {status[value]?.svg}
          <span style={{ color: status[value]?.color, fontWeight: 500 }}>
            {status[value]?.text}
          </span>
        </div>
      </TableCell>
    )
  }

  return (
    <TableCell
      align='left'
      sx={{
        backgroundColor: getBGColors({
          data: value,
          isSleeping: props?.isSleeping,
        }),
        ...props?.sxx,
      }}
      {...props}
    >
      {value ? value : '-'}
    </TableCell>
  )
}

const TYPES = {
  dropdown: ({ data, optionType, onChange, ...props }) => (
    <DropdownTableCell
      data={data}
      optionType={optionType}
      onChange={onChange}
      {...props}
    />
  ),
  date: ({ data, ...props }) => (
    <DateTableCell
      date={data}
      {...props}
    />
  ),
  text: ({ data, ...props }) => (
    <TextTableCell
      text={data}
      {...props}
    />
  ),
  hour: ({ data, ...props }) => (
    <HourTableCell
      hour={data}
      {...props}
    />
  ),
  action: ({ data, ...props }) => (
    <ActionTableCell
      action={data}
      {...props}
    />
  ),
  value: ({ data, ...props }) => {
    return (
      <ValueTableCell
        value={data}
        {...props}
      />
    )
  },
  blank: ({ ...props }) => {
    return (
      <TableCell
        sx={{ borderColor: '#FFF' }}
        {...props}
      />
    )
  },
}

const CustomTableCell = ({ data, type = 'text', ...props }) =>
  TYPES[type]({ data, ...props })

export default CustomTableCell
